/**
 * Registro de proveedores **DEMO** (client-side) en el runtime selector
 * de la Capa Operacional AI-First (`../api/runtime`).
 *
 * El runtime selector (tarea 1.2) resuelve, vía {@link provideDemo} /
 * {@link provideConnected}, qué implementación concreta de cada interfaz
 * de servicio se usa. Hasta ahora ninguna implementación se registraba,
 * de modo que `runtime.motorCasos()` devolvía el stub "no implementado".
 *
 * Este módulo enchufa las implementaciones client-side ya construidas en
 * `frontend/src/lib/` —Event_Store (1.3), Grafo_Entidades (1.7) y
 * Motor_Casos (2.1, 2.2, 2.7)— detrás de su contrato, sin tocar las
 * vistas ni el selector (R17.1, R17.2, R17.5). La conmutación a backend
 * real (CONECTADO) se añadirá registrando `provideConnected` para las
 * mismas claves; las vistas no cambian.
 *
 * El registro es **idempotente**: {@link ensureDemoProviders} solo
 * registra una vez por sesión, y comparte un único {@link EventStoreLocal}
 * entre el Motor_Casos y el resto de la Capa para que el timeline y la
 * cadena hash sean consistentes (R9).
 */
import { provideDemo, runtime } from "../api/runtime";
import { EventStoreLocal } from "./eventStore";
import { GrafoEntidadesLocal } from "./graph";
import { MemoriaOperacionalLocal } from "./memoria";
import { MotorCasosLocal } from "./motorCasos";
import { crearRegistroAcciones } from "./registry";
import { RAGLocal } from "./rag";

let registrado = false;

/**
 * Registra (una sola vez) las implementaciones DEMO de los servicios de
 * la Capa que ya existen client-side. Es seguro llamarla múltiples veces.
 */
export function ensureDemoProviders(): void {
  if (registrado) return;
  registrado = true;

  // Event_Store append-only con cadena hash (IndexedDB en navegador,
  // memoria en Node/Vitest). Singleton compartido por la Capa (R9).
  provideDemo("eventStore", () => new EventStoreLocal());

  // Grafo_Entidades en memoria. El Motor_Casos resuelve la entidad raíz
  // con `rootOf` (jerarquía), por lo que un grafo vacío es suficiente
  // para la correlación; las vistas que proyectan el grafo lo poblarán.
  provideDemo("graph", () => new GrafoEntidadesLocal());

  // Motor_Casos cableado al Event_Store demo: emite un Evento_Operacional
  // por cada delta de Caso (R9) y soporta el timeline desde el log.
  provideDemo(
    "motorCasos",
    () => new MotorCasosLocal({ eventStore: runtime.eventStore() }),
  );

  // Base_Conocimiento_RAG demo: embeddings pre-computados deterministas +
  // similitud coseno pura, indexación atómica y round-trip de metadatos (R13).
  provideDemo("rag", () => new RAGLocal());

  // Registro_Acciones_Semanticas: catálogo declarativo versionado con
  // round-trip exacto (declare/get) y clasificación de criticidad (R12.2,
  // R18.1, R20.3) más el ejecutor con gate de permisos/políticas y HITL
  // (R12.4–R12.7, R18.2–R18.6, R20.1–R20.3). Comparte el Event_Store de la
  // Capa para registrar invocaciones, denegaciones, aprobaciones,
  // reversiones y expiraciones en la misma cadena hash (R9, R12.4).
  provideDemo("registry", () =>
    crearRegistroAcciones(undefined, { eventStore: runtime.eventStore() }),
  );

  // Memoria_Operacional: decisiones·recomendaciones·outcomes persistidos
  // en IndexedDB/localStorage/memoria; tasas deterministas en [0,1] (R10).
  provideDemo("memoria", () => new MemoriaOperacionalLocal());
}
